"use client";

import {
  CopyButton,
  DataTable,
  ErrorNotice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { MicroscopeMagnificationCalculatorStrings } from "@/components/tools/microscope-magnification-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  type NumberFormatter,
  plain,
  sig,
  toTsv,
} from "@/lib/tool-kit/biology-lab/format";
import {
  COMMON_OBJECTIVES,
  cellSizeUm,
  fieldAreaMm2,
  fieldOfViewMm,
  friendlyUnit,
  fromMicrons,
  fromScaleBar,
  type IamTarget,
  type LengthUnit,
  type MicroscopeError,
  solveIam,
  totalMagnification,
} from "@/lib/tool-kit/biology-lab/microscope";

type Strings = MicroscopeMagnificationCalculatorStrings;

const WORKING_BOX =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";

export function unitLabel(unit: LengthUnit, strings: Strings): string {
  const labels: Record<LengthUnit, string> = {
    nm: strings.unitNm,
    um: strings.unitUm,
    mm: strings.unitMm,
    cm: strings.unitCm,
  };
  return labels[unit];
}

/** A length in microns written in the friendliest unit: "90 µm". */
export function lengthText(
  fmt: NumberFormatter,
  microns: number,
  strings: Strings
): string {
  const unit = friendlyUnit(microns);
  return `${sig(fmt, fromMicrons(microns, unit), 4)} ${unitLabel(unit, strings)}`;
}

function errorText(code: MicroscopeError, strings: Strings): string {
  const messages: Record<MicroscopeError, string> = {
    objective: strings.errObjective,
    eyepiece: strings.errEyepiece,
    auxiliary: strings.errAuxiliary,
    fieldNumber: strings.errFieldNumber,
    image: strings.errImage,
    actual: strings.errActual,
    magnification: strings.errMagnification,
    bar: strings.errBar,
    barValue: strings.errBarValue,
    feature: strings.errFeature,
    cells: strings.errCells,
  };
  return messages[code];
}

export function Failure({
  code,
  strings,
}: {
  code: MicroscopeError;
  strings: Strings;
}) {
  return <ErrorNotice>{errorText(code, strings)}</ErrorNotice>;
}

function Working({ lines, strings }: { lines: string[]; strings: Strings }) {
  return (
    <div className={WORKING_BOX}>
      <p className="font-medium font-sans text-foreground">{strings.working}</p>
      {lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}

export function MagnificationResult({
  objective,
  eyepiece,
  auxiliary,
  fieldNumber,
  strings,
  fmt,
}: {
  objective: number;
  eyepiece: number;
  auxiliary: number;
  fieldNumber: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const total = totalMagnification(objective, eyepiece, auxiliary);
  const fov = fieldOfViewMm(fieldNumber, objective, auxiliary);
  const head = [
    strings.colObjective,
    strings.colTotal,
    strings.colFov,
    strings.colFovUm,
  ];
  const rows = COMMON_OBJECTIVES.map((o) => {
    const f = fieldOfViewMm(fieldNumber, o, auxiliary);
    return [
      `${o}×`,
      `${plain(fmt, totalMagnification(o, eyepiece, auxiliary))}×`,
      sig(fmt, f, 3),
      sig(fmt, f * 1000, 3),
    ];
  });
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.totalMagnification}
          value={`${sig(fmt, total, 4)}×`}
        />
        <StatCard label={strings.fovMm} value={sig(fmt, fov, 4)} />
        <StatCard label={strings.fovUm} value={sig(fmt, fov * 1000, 4)} />
        <StatCard
          label={strings.fieldArea}
          value={sig(fmt, fieldAreaMm2(fov), 4)}
        />
      </StatGrid>
      <Working
        lines={[
          t(strings.workingTotal, {
            obj: plain(fmt, objective),
            eye: plain(fmt, eyepiece),
            aux: plain(fmt, auxiliary),
            result: sig(fmt, total, 4),
          }),
          t(strings.workingFov, {
            fn: plain(fmt, fieldNumber),
            obj: plain(fmt, objective),
            aux: plain(fmt, auxiliary),
            result: sig(fmt, fov, 4),
          }),
        ]}
        strings={strings}
      />
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
        <DataTable head={head} rows={rows} />
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyTable}
          small
          text={toTsv(head, rows)}
        />
      </div>
    </>
  );
}

export function IamResult({
  target,
  actualUm,
  imageUm,
  magnification,
  strings,
  fmt,
}: {
  target: IamTarget;
  actualUm: number | null;
  imageUm: number | null;
  magnification: number | null;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const value = solveIam(target, actualUm, imageUm, magnification);
  if (value === null) {
    return <ErrorNotice>{strings.errIam}</ErrorNotice>;
  }
  if (!(value > 0 && Number.isFinite(value))) {
    return <ErrorNotice>{strings.errPositive}</ErrorNotice>;
  }
  const shown =
    target === "magnification"
      ? `${sig(fmt, value, 4)}×`
      : lengthText(fmt, value, strings);
  const labels: Record<IamTarget, string> = {
    actual: strings.resultActual,
    image: strings.resultImage,
    magnification: strings.resultMagnification,
  };
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={labels[target]} value={shown} />
        {target === "actual" ? (
          <StatCard
            label={strings.inMicrons}
            value={`${sig(fmt, value, 4)} ${strings.unitUm}`}
          />
        ) : null}
      </StatGrid>
      <Working lines={[strings.workingIam]} strings={strings} />
    </>
  );
}

export function ScaleBarResult({
  barMeasured,
  barUm,
  featureMeasured,
  measureUnitMm,
  measureLabel,
  strings,
  fmt,
}: {
  barMeasured: number;
  barUm: number;
  featureMeasured: number;
  measureUnitMm: number | null;
  measureLabel: string;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  if (!(barMeasured > 0)) {
    return <Failure code="bar" strings={strings} />;
  }
  if (!(barUm > 0)) {
    return <Failure code="barValue" strings={strings} />;
  }
  if (!(featureMeasured > 0)) {
    return <Failure code="feature" strings={strings} />;
  }
  const r = fromScaleBar(barMeasured, barUm, featureMeasured, measureUnitMm);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.resultActual}
          value={lengthText(fmt, r.actualUm, strings)}
        />
        <StatCard
          label={t(strings.perMeasure, { unit: measureLabel })}
          value={`${sig(fmt, r.umPerMeasure, 4)} ${strings.unitUm}`}
        />
        {r.magnification === null ? null : (
          <StatCard
            label={strings.resultMagnification}
            value={`${sig(fmt, r.magnification, 4)}×`}
          />
        )}
      </StatGrid>
      <Working
        lines={[
          t(strings.workingBar, {
            feature: plain(fmt, featureMeasured),
            bar: plain(fmt, barMeasured),
            barUm: plain(fmt, barUm),
            result: sig(fmt, r.actualUm, 4),
          }),
        ]}
        strings={strings}
      />
    </>
  );
}

export function CellsResult({
  fieldNumber,
  objective,
  auxiliary,
  cells,
  strings,
  fmt,
}: {
  fieldNumber: number;
  objective: number;
  auxiliary: number;
  cells: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const fovUm = fieldOfViewMm(fieldNumber, objective, auxiliary) * 1000;
  const size = cellSizeUm(fovUm, cells);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.cellLength}
          value={lengthText(fmt, size, strings)}
        />
        <StatCard label={strings.fovUm} value={sig(fmt, fovUm, 4)} />
      </StatGrid>
      <Working
        lines={[
          t(strings.workingCells, {
            fov: sig(fmt, fovUm, 4),
            n: plain(fmt, cells),
            result: sig(fmt, size, 4),
          }),
        ]}
        strings={strings}
      />
    </>
  );
}
